<template>
    <div class="w:full xl:w-3/4">
        
        <div class="flex gap-x-4 justify-between items-end">
            <!-- Substitutions -->
            <div v-if="order.allow_substitutions" class="pb-1">
                <span class="font-bold">Substitutions Allowed</span>
                <div class="text-xs">If an item is unavailable when preparing your order, we may substitute some or all of it for a similar item.</div>
            </div>
            <div v-else class="pb-1">
                <span class="font-bold">Substitutions Not Allowed</span>
                <div class="text-xs">If an item is unavailable when preparing your order, we will only give what's available without substituting for a similar item.</div>
            </div>

            <div v-if="!isCustomer && (order.checker_id || order.picker_id)" class="text-right">
                <!-- Checked By -->
                <div v-if="!isCustomer && order.checker_id" class="font-bold">
                    Checked by {{ order.checker.name }}
                    <div v-if="order.picking_checked_at" class="text-xs">{{ formatTimestamp(order.picking_checked_at) }}</div>
                </div>

                <!-- Picked By -->
                <div v-if="!isCustomer && order.picker_id" class="font-bold">
                    Picked by {{ order.picker.name }}
                </div>
            </div>
        </div>

        <div class="rounded-t overflow-hidden">
            <table class="w-full">
                <thead>
                    <tr class="h-10 align-bottom bg-gray-light border">
                        <th class="px-2 py-1 text-center">BC</th>
                        <th v-if="!isCustomer" class="px-2 py-1 text-left whitespace-nowrap">Item #</th>
                        <th class="px-2 py-1 text-left md:w-1/3">Product</th>
                        <th class="hidden md:table-cell px-2 py-1 text-left w-1/3">Notes</th>
                        <th class="hidden sm:table-cell px-2 py-1 text-center">Price</th>
                        <th class="hidden sm:table-cell px-2 py-1 text-center">Quantity</th>
                        <th class="sm:hidden px-2 py-1 text-center whitespace-nowrap">Qty @ Price</th>
                        <th class="px-2 py-1 text-right whitespace-nowrap">Line Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in order.order_items" :key="item.id"
                        class="border striped"
                        :class="{
                            'item_picked': !isCustomer && item.checking_status == 'picked',
                            'item_shorted': !isCustomer && item.checking_status == 'shorted',
                        }"
                    >
                        <td class="px-2 py-1">
                            <div>
                                <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(item) == 'no_error'" width="24" height="24" class="mx-auto" />
                                <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(item) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessageBC(item.bc_error)" />
                            </div>
                        </td>
                        <td v-if="!isCustomer" class="px-2 py-1">
                            {{ item.product_id }}
                        </td>
                        <td class="px-2 py-1">
                            <div>
                                {{ item.title }}
                                <span v-if="item.description"> - {{ item.description }}</span>
                                <span v-if="item.size"> - {{ item.size }}</span>
                                <span v-if="item.product.special_order" class="ml-1 text-xs italic text-red">(Special Order Item)</span>
                            </div>
                        </td>
                        <td class="hidden md:table-cell px-2 py-1">
                            {{ item.product_notes }}
                            <div v-if="!isCustomer && item.picker_id" class="text-xs italic">
                                Picked by <b>{{ item.picker.name }}</b>
                                <span v-if="item.picked_at"> at {{ formatTimestamp(item.picked_at) }}</span>
                            </div>
                            <div v-if="!isCustomer && item.checker_id" class="text-xs italic">
                                Checked by <b>{{ item.checker.name }}</b>
                                <span v-if="item.checked_at"> at {{ formatTimestamp(item.checked_at) }}</span>
                            </div>
                            <div v-if="!isCustomer && item.checking_status == 'shorted' && item.auth_code" class="text-xs italic">
                                Short authorized by <b>{{ item.auth_code.name }}</b>.
                            </div>
                        </td>
                        <td class="hidden sm:table-cell px-2 py-1 text-center">{{ toCurrency(item.price) }}</td>
                        <td class="hidden sm:table-cell px-2 py-1 text-center">{{ item.quantity }}</td>
                        <td class="sm:hidden px-2 py-1 text-center">{{ item.quantity }} @ ${{item.price}}</td>
                        <td class="px-2 py-1 text-right whitespace-nowrap">{{ toCurrency(item.price * item.quantity) }}</td>
                    </tr>
                    <tr>
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="text-right font-bold px-2 pt-2 pb-1">Subtotal:</td>
                        <td class="text-right px-2 pt-2 pb-1">{{ toCurrency(subtotal) }}</td>
                    </tr>
                    <tr>
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="text-right font-bold px-2 pt-2 pb-1">Delivery Fee:</td>
                        <td class="text-right px-2 pt-2 pb-1">{{ toCurrency(delivery_fee) }}</td>
                    </tr>
                    <tr v-if="!isSelectedCustomerTaxExempt">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="px-2 py-1 text-right font-bold">Tax:</td>
                        <td class="px-2 py-1 text-right">{{ toCurrency(tax) }}</td>
                    </tr>
                    <tr v-if="order.applied_promo_code">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="px-2 py-1 text-right">
                            <div class="font-bold">Promo Code:</div>
                            <div class="font-grey-500">
                                {{ order.applied_promo_code.code }}
                                <span v-if="order.promo_code_percent">({{ order.promo_code_percent }}%)</span>
                            </div>
                        </td>
                        <td class="px-2 py-1 text-right whitespace-nowrap">- {{ toCurrency(order.promo_code_total) }}</td>
                    </tr>
                    <tr v-if="order.applied_credit_amount > 0">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="px-2 py-1 text-right font-bold">Prepaid:</td>
                        <td class="px-2 py-1 text-right">{{ toCurrency(parseFloat(order.applied_credit_amount)) }}</td>
                    </tr>
                    <tr>
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="px-2 py-1 text-right font-bold">Total:</td>
                        <td class="px-2 py-1 text-right">{{ toCurrency(total) }}</td>
                    </tr>
                    <tr v-if="order.exported_at === null && !is_past_ship_date">
                        <td colspan="1"></td>
                        <td v-if="!isCustomer" colspan="1"></td>
                        <td colspan="1" class="hidden sm:table-cell"></td>
                        <td colspan="1" class="hidden md:table-cell"></td>
                        <td colspan="2" class="px-2 py-1 text-right font-bold">Lock in pricing:</td>
                        <td class="px-2 py-1 text-right">{{ order.is_pricing_locked ? 'Yes' : 'No' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import { DateTime } from "luxon";

export default {

    props: ['order'],

    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return {
            isAdmin,
            isCustomer,
        }
    },

    computed: {
        isSelectedCustomerTaxExempt () {
            return (this.order.customer && this.order.customer.tax_exempt) ? true : false;
        },
        subtotal() {
            return this.roundDecimals(this.order.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.quantity * currentValue.price);
            }, 0));
        },
        tax() {
            if (this.isSelectedCustomerTaxExempt) return 0;

            let taxRatio = 0.13;
            let tax = this.order.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.tax === true ?  (currentValue.quantity * currentValue.price * taxRatio) : 0);
            }, 0);

            return this.roundDecimals(tax + (this.delivery_fee * taxRatio));
        },
        total() {
            return this.roundDecimals(this.subtotal + this.tax + this.delivery_fee - parseFloat(this.order.promo_code_total ?? 0) - parseFloat(this.order.applied_credit_amount ?? 0));
        },
        delivery_fee(){
            return parseFloat(this.order.fee_for_delivery ?? 0);
        },
        is_past_ship_date() {
            return new Date(this.order.ship_date) < new Date();
        }
    },

    methods: {
        BCInfoFilled (order) {
            if (order.bc_error != null)
                return 'error';
            else if (order.bc_id != null && order.bc_error == null)
                return 'no_error';
            else 
                return false;
        },

        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },

        formatTimestamp(timestamp) {
            return DateTime.fromSQL(timestamp).toFormat('MMM. d, yyyy h:mm a');
        },
    }
}
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
</style>
